// Runs this participant's side of a take. When the host presses record the
// server picks a start time a few seconds ahead; every participant starts
// recording at that moment by the server's clock, so tracks line up. Each
// kind (audio, video) is its own recorder and upload track. Joining while a
// take is running starts recording straight away (a new segment).

import { highResNow } from './clock-sync';
import { pickMime, TrackRecorder, type TrackKind } from './recorder';
import type { UploadQueue } from './upload-queue';

type Msg = { type: string } & Record<string, unknown>;

export interface RecChannel {
  send(msg: Record<string, unknown>): void;
  on(type: string, fn: (msg: Msg) => void): () => void;
}

export interface RecClock {
  readonly offset: number;
  readonly rtt: number | null;
  localAt(serverMs: number): number;
  serverNow(): number;
}

export interface Take {
  id: string;
  takeNumber: number;
  startAtServerMs: number;
}

export type RecPhase = 'idle' | 'countdown' | 'recording';

export interface RecView {
  phase: RecPhase;
  take: Take | null;
  /** The take this participant last recorded (for upload progress). */
  lastTakeId: string | null;
  error: string | null;
}

export interface RecordingControllerOptions {
  channel: RecChannel;
  clock: RecClock;
  stream: MediaStream;
  participantId: string;
  queue: Pick<UploadQueue, 'addTrack' | 'addChunk' | 'finish'>;
  onChange?: (view: RecView) => void;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (t: unknown) => void;
  isTypeSupported?: (mime: string) => boolean;
  Recorder?: typeof MediaRecorder;
  newId?: () => string;
}

export const REC_ERRORS: Record<string, string> = {
  already_recording: 'A take is already recording.',
  not_recording: 'Nothing is recording.',
  host_only: 'Only the host can start and stop recording.',
  no_format: 'This browser can’t record here. Try Chrome, Edge or Firefox.',
  save_failed: 'Couldn’t save the recording on this computer. Is the disk full?',
};

interface Active {
  id: string;
  rec: TrackRecorder;
  /** Saves happen in order: the track first, then each chunk. */
  saved: Promise<void>;
}

export class RecordingController {
  private view: RecView = { phase: 'idle', take: null, lastTakeId: null, error: null };
  private active: Active[] = [];
  private offs: Array<() => void> = [];
  private timer: unknown = null;
  private ending: Promise<void> = Promise.resolve();

  constructor(private o: RecordingControllerOptions) {}

  get state(): RecView {
    return { ...this.view };
  }

  private set(patch: Partial<RecView>) {
    this.view = { ...this.view, ...patch };
    this.o.onChange?.(this.state);
  }

  start(): void {
    const on = (type: string, fn: (m: Msg) => void) => this.offs.push(this.o.channel.on(type, fn));
    on('welcome', (m) => {
      if (m.session) this.begin(m.session as Take);
    });
    on('rec.started', (m) => this.begin(m.session as Take));
    on('rec.stopped', () => void this.end());
    on('error', (m) => {
      const code = String(m.error);
      if (code in REC_ERRORS) this.set({ error: REC_ERRORS[code]! });
    });
  }

  /** Host: everyone starts recording a moment from now. */
  requestStart(): void {
    this.set({ error: null });
    this.o.channel.send({ type: 'rec.start' });
  }

  requestStop(): void {
    this.o.channel.send({ type: 'rec.stop' });
  }

  /** ms recorded so far, by the server clock. */
  elapsedMs(): number {
    const take = this.view.take;
    return take ? Math.max(0, this.o.clock.serverNow() - take.startAtServerMs) : 0;
  }

  /** ms until recording starts (countdown). */
  countdownMs(): number {
    const take = this.view.take;
    return take ? Math.max(0, take.startAtServerMs - this.o.clock.serverNow()) : 0;
  }

  private begin(take: Take) {
    if (this.view.take?.id === take.id) return;
    this.clearTimer();
    const wait = this.o.clock.localAt(take.startAtServerMs) - (this.o.now ?? highResNow)();
    this.set({ phase: wait > 0 ? 'countdown' : 'recording', take, lastTakeId: take.id, error: null });
    if (wait > 0) {
      this.timer = (this.o.setTimer ?? ((fn, ms) => setTimeout(fn, ms)))(() => {
        this.timer = null;
        this.record(take);
      }, wait);
    } else {
      this.record(take);
    }
  }

  private record(take: Take) {
    const { stream, clock, queue, participantId } = this.o;
    const isSupported = this.o.isTypeSupported ?? ((m: string) => MediaRecorder.isTypeSupported(m));
    const kinds: TrackKind[] = (['audio', 'video'] as const).filter((k) =>
      k === 'audio' ? stream.getAudioTracks().length > 0 : stream.getVideoTracks().length > 0,
    );
    this.set({ phase: 'recording' });
    for (const kind of kinds) {
      const mime = pickMime(kind, isSupported);
      if (!mime) {
        this.set({ error: REC_ERRORS.no_format! });
        continue;
      }
      const id = (this.o.newId ?? (() => crypto.randomUUID()))();
      const label = (kind === 'audio' ? stream.getAudioTracks() : stream.getVideoTracks())[0]?.label || null;
      const entry: Active = { id, rec: null as unknown as TrackRecorder, saved: Promise.resolve() };
      const save = (fn: () => Promise<void>) => {
        entry.saved = entry.saved.then(fn).catch(() => this.set({ error: REC_ERRORS.save_failed! }));
      };
      entry.rec = new TrackRecorder({
        stream,
        kind,
        mime,
        Recorder: this.o.Recorder,
        now: this.o.now,
        onStart: (localMs) =>
          save(() =>
            queue.addTrack({
              id,
              sessionId: take.id,
              participantId,
              kind,
              mime,
              startedAtServerMs: localMs + clock.offset,
              clockOffsetMs: clock.offset,
              clockRttMs: clock.rtt,
              deviceLabel: label,
              registered: false,
              finalSeq: null,
              endedAtServerMs: null,
            }),
          ),
        onChunk: (seq, blob) => save(() => queue.addChunk(id, seq, blob)),
        onError: () => this.set({ error: REC_ERRORS.save_failed! }),
      });
      this.active.push(entry);
      entry.rec.start();
    }
  }

  /** Stops recording; resolves once every chunk is saved for upload. */
  end(): Promise<void> {
    this.clearTimer();
    const active = this.active.splice(0);
    // Recording ends now, by the server clock (stopping takes a moment more).
    const endedAt = this.o.clock.serverNow();
    if (this.view.phase !== 'idle') this.set({ phase: 'idle', take: null });
    this.ending = this.ending.then(() =>
      Promise.all(
        active.map(async (a) => {
          const finalSeq = await a.rec.stop();
          await a.saved;
          await this.o.queue.finish(a.id, finalSeq, endedAt);
        }),
      ).then(() => undefined),
    );
    return this.ending;
  }

  private clearTimer() {
    if (this.timer !== null) (this.o.clearTimer ?? ((t) => clearTimeout(t as ReturnType<typeof setTimeout>)))(this.timer);
    this.timer = null;
  }

  /** Leaving: stop listening and finish whatever is recording. */
  close(): Promise<void> {
    this.offs.splice(0).forEach((off) => off());
    return this.end();
  }
}
